import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HotTableRegisterer } from '@handsontable/angular';
import Handsontable from 'handsontable';
import { format } from 'date-fns';
import { forkJoin, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Message } from 'src/app/core/class/message';
import { HandsontableFieldTypeContrants } from 'src/app/core/constants/handsontable-field-types';
import { CommonService } from 'src/app/core/services/common.service';
import { CreateContractService } from 'src/app/core/services/create-contract.service';
import { NegotiationHandsontableService } from 'src/app/core/services/negotiation-handsontable.service';
import { TemplateFormService } from 'src/app/core/services/template-form.service';
import { BaseComponent } from 'src/app/shared/components/base/base.component';
import { FormData } from 'src/app/shared/models/form-data.model';
import { NegotiationDialogApi } from 'src/app/core/services/negotiation-dialog-api';

const DATA_TYPE_REF_CUSTOMER = "tham_chieu_ben_khach_hang";
const DATA_TYPE_SELECT = "select";
const DATA_TYPE_CUSTOMER = "customer";
const CATEGORY_CUSTOMER_CODE = 'customer';
const TYPE_COMBOBOX = 'combobox';
const DATA_TYPE_REF = 'ref';
const CONTRACT_CODE_TYPE = 'contract_code'
const LIST_FIELD_CAN_CHANGE = ['email', 'phone', 'customerId', 'order'];

@Component({
    selector: 'app-negotiation-form-template',
    templateUrl: './negotiation-form-template.component.html',
    styleUrls: ['./negotiation-form-template.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('void', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('*', style({ height: '*', visibility: 'visible' })),
            transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class NegotiationFormTemplateComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() negotiationMode = true;
    @Input() negotiationCustomerIds: string[] = [];
    public referenceApi: NegotiationDialogApi;
    @Output() formReady = new EventEmitter<void>();
    @Output() formLoadError = new EventEmitter<void>();
    private destroyed = false;

    contract: any;
    isOnAutoNumber:  boolean = false;
    private _hotSettingsDefault: Handsontable.GridSettings = {
        stretchH: 'all',
        rowHeaders: false,
        colWidths: [50, 150, 250, 150, 200],
        minCols: 5,
        maxCols: 5,
        minSpareCols: 0,
        hiddenColumns: { columns: [4], indicators: false },
        columns: [
            {
                readOnly: true
            },
            {
                readOnly: true
            },
            {
                readOnly: false
            },
            {
                readOnly: true,
                renderer:function(instance, td, row, col, prop){
                    //Handsontable.renderers.HtmlRenderer.apply(this, arguments);
                    //console.log(td,row,col);
                    return td;
                }
            },
            {
                readOnly: true
            }
        ],
        formulas: true,
        colHeaders: ['STT', 'Tên', 'Giá trị', 'Ghi chú', 'Bên nhập'],
        data: [],
        licenseKey: 'non-commercial-and-evaluation',
        height: 'calc(91vh - 189px - 1rem)',
        width: '100%',
        undo: true,
        hiddenRows: {
            rows: [],
            indicators: true
        },
        comments: true,
        contextMenu: false,
        rowHeights: '40px',
        columnHeaderHeight: 40,
        mergeCells: true
    }

    public modifiedTableSettings: Handsontable.GridSettings = {
        ...this._hotSettingsDefault
    };

    private _hotRegister = new HotTableRegisterer();

    private recordUserSign: any[] = [];

    private data: FormData;

    subscriptions: Subscription = new Subscription();
    public certificateType: any[] = [];
    public show: boolean = true;
    public configAutoNumber = '';
    isBack: boolean = false;
    isUpdate: any = false;
    refreshContractCode: boolean = false;
    isChangeTypeDocAndTemp: boolean = false;
    constructor(
        public commonService: CommonService,
        public message: Message,
        public handsontableService: NegotiationHandsontableService,
        public templateFormService: TemplateFormService,
        public createContractService: CreateContractService
    ) {
        super(commonService, message);
        this.isOnAutoNumber = this.createContractService.isOnAutoNumber.getValue();
        this.configAutoNumber = this.createContractService.configAutoNumber.getValue();
    }

    ngOnInit() {
        this.subscribeValue();
        this.contract = this.createContractService.document.getValue();
        this.isBack = this.createContractService.backView.getValue();
    }

    subscribeValue () {
        this.subscriptions.add(
            this.createContractService.document.asObservable().subscribe(rs => {
              this.contract = rs;
            })
        );
        this.subscriptions.add(
            this.createContractService.isOnAutoNumber.asObservable().subscribe(rs => {
              this.isOnAutoNumber = rs;
            })
        );
    }

    ngAfterViewInit(): void {
        this.subscriptions.add(this.handsontableService.dirty.subscribe(ref => {
            if (ref) {
                this.createContractService.isDirty = true;
            }
        }));
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
    }

    processDataWithModel(data: FormData, users: any) {
        this.handsontableService.negotiationFieldAccess = this.negotiationMode
            ? field => this.canEditNegotiationField(field) : null;
        if (this.negotiationMode) {
            // Group controls must not insert or remove fields assigned to other parties.
            const disableGroupControls = (rows: any[]) => (rows || []).forEach(row => {
                row.duplicate = false;
                if (Array.isArray(row.children) && row.children.length === 0 &&
                    !(row.field && row.field.fieldConfig && row.field.fieldConfig.group)) {
                    row.children = null;
                }
                disableGroupControls(row.children);
            });
            disableGroupControls(data.rows);
        }
        const customers = users.filter(user => user && !user.isCreatedSide &&
            (!this.negotiationMode || user.customerId));
        const customerIds = customers.map(customer => {
            return customer.customerId
        });
        const rows = data.rows;
        rows.forEach(row => {
            const cellValue = row.cells && row.cells.value ? row.cells.value : null;
            if (cellValue && cellValue.valueId && cellValue.category && cellValue.category.code === CATEGORY_CUSTOMER_CODE) {
                if (!customerIds.includes(cellValue.valueId)) {
                    customerIds.push(cellValue.valueId);
                    customers.push({ name: cellValue.value, customerId: cellValue.valueId });
                }
            }
            if (row.children) {
                row.children.forEach(child => {
                    const cellValueChild = child.cells && child.cells.value ? child.cells.value : null;
                    if (cellValueChild && cellValueChild.valueId && cellValueChild.category && cellValueChild.category.code === CATEGORY_CUSTOMER_CODE) {
                        if (!customerIds.includes(cellValueChild.valueId)) {
                            customerIds.push(cellValueChild.valueId);
                            customers.push({ name: cellValueChild.value, customerId: cellValueChild.valueId });
                        }
                    }
                })
            }
        });
        const needsCustomers = this.requiresCustomerReferenceData(data.rows);
        const emptyResponse = { data: [] };
        const customerRequest = needsCustomers && customerIds.length
            ? this.referenceApi ? this.referenceApi.getCustomers(customerIds) : this.templateFormService.getCustomers(customerIds)
            : of(emptyResponse);
        this.subscriptions.add(customerRequest.pipe(switchMap(response => {
            // Only resolve certificate labels when the customer payload actually contains those IDs.
            const referenceData = response && response.data || [];
            return forkJoin({
                customers: of(response),
                certificate: referenceData.some(user => user && user.certificateId)
                    ? this.referenceApi ? this.referenceApi.getCertificateType() : this.commonService.getCertificateType()
                    : of(emptyResponse),
                representativeCertificate: referenceData.some(user => user && user.representativeCertificateId)
                    ? this.referenceApi ? this.referenceApi.getRepresentativeCertificateType() : this.commonService.getRepresentativeCertificateType()
                    : of(emptyResponse)
            });
        })).subscribe(res => {
            if (this.destroyed) {
                return;
            }
            const certificateType = res.certificate && res.certificate.data ? res.certificate.data : [];
            const representativeCertificate = res.representativeCertificate && res.representativeCertificate.data
                ? res.representativeCertificate.data : [];
            this.certificateType = certificateType.concat(representativeCertificate);
            this.recordUserSign = this.mapDataCustomer(customers, res.customers && res.customers.data ? res.customers.data : []);
            // Defer ra tick sau: khi master-data hit cache, forkJoin emit đồng bộ trong ngOnInit,
            // lúc đó <hot-table> (ngIf) chưa được tạo -> getInstance('negotiation-template') = undefined.
            setTimeout(() => {
                if (this.destroyed) {
                    return;
                }
                try {
                    this.fillDataToModel(this.recordUserSign, data);
                } catch (error) {
                    console.error('Unable to populate contract template table', error);
                    this.formLoadError.emit();
                }
            });
        }, error => {
            console.error('Unable to load contract template reference data', error);
            this.formLoadError.emit();
        }));
    }

    private requiresCustomerReferenceData(rows: any[]): boolean {
        return (rows || []).some(row => {
            const cell = row.cells && row.cells.value;
            const config = row.field && row.field.fieldConfig;
            const customerSelector = (cell && cell.category && cell.category.code === CATEGORY_CUSTOMER_CODE) ||
                (config && config.category && config.category.code === CATEGORY_CUSTOMER_CODE);
            const customerReference = (cell && cell.dataType && cell.dataType.code === DATA_TYPE_REF_CUSTOMER) ||
                (config && config.dataType && config.dataType.code === DATA_TYPE_REF_CUSTOMER);
            return (this.canEditNegotiationField(row.field) && !!(customerSelector || customerReference)) ||
                this.requiresCustomerReferenceData(row.children);
        });
    }

    private readStoredJson(value: any): any {
        if (typeof value !== 'string') {
            return value;
        }
        try {
            return JSON.parse(value);
        } catch (_) {
            return null;
        }
    }

    private readStoredArray(value: any): any[] {
        const parsed = this.readStoredJson(value);
        return Array.isArray(parsed) ? parsed : [];
    }

    private isStoredOption(value: any): boolean {
        return !!(value && typeof value === 'object' && !Array.isArray(value) &&
            (typeof value.id === 'string' || typeof value.id === 'number') &&
            (typeof value.value === 'string' || typeof value.value === 'number' || typeof value.value === 'boolean'));
    }

    private sameSelectionId(left: any, right: any): boolean {
        return (typeof left === 'string' || typeof left === 'number') &&
            (typeof right === 'string' || typeof right === 'number') && String(left) === String(right);
    }

    private restoreSelectionCell(cell: any, rowIndex: number, dataChange: any[]): void {
        const type = cell && cell.type && cell.type.code;
        if (![HandsontableFieldTypeContrants.combobox, HandsontableFieldTypeContrants.checkbox,
            HandsontableFieldTypeContrants.radio].includes(type)) {
            return;
        }
        cell.data = Array.isArray(cell.data)
            ? cell.data.filter(option => option && typeof option === 'object' && !Array.isArray(option) &&
                ['string', 'number', 'boolean'].includes(typeof option.value) &&
                (type === HandsontableFieldTypeContrants.combobox || this.isStoredOption(option))) : [];
        if (type === HandsontableFieldTypeContrants.combobox) {
            // A literal label may itself start with '{' or look like JSON.
            if (cell.data.some(option => option.value === cell.value)) {
                return;
            }
            // Plain labels are valid too; only serialized objects need decoding.
            if (cell.value && (typeof cell.value === 'object' ||
                (typeof cell.value === 'string' && cell.value.trim().charAt(0) === '{'))) {
                const stored = this.readStoredJson(cell.value);
                const option = cell.data.find(item => this.sameSelectionId(item.id, cell.valueId));
                const label = stored && !Array.isArray(stored) &&
                    ['string', 'number', 'boolean'].includes(typeof stored.value) ? stored.value : null;
                cell.value = option ? option.value : label;
                if (option && label !== option.value) {
                    dataChange.push([rowIndex, 2, option.value]);
                }
            }
            if (cell.value == null) {
                // Do not recreate a fallback option from an invalid label and a stale ID.
                cell.valueId = null;
            }
            return;
        }
        if (type === HandsontableFieldTypeContrants.checkbox) {
            const ids = this.readStoredArray(cell.valueId);
            const storedOptions = this.readStoredArray(cell.value).filter(option => this.isStoredOption(option));
            const selected = [];
            ids.forEach(id => {
                if (typeof id !== 'string' && typeof id !== 'number') {
                    return;
                }
                const existing = cell.data.find(option => this.sameSelectionId(option.id, id));
                const option = existing || storedOptions.find(item => this.sameSelectionId(item.id, id));
                if (!option) {
                    return;
                }
                if (!existing) {
                    cell.data.push(option);
                }
                if (!selected.some(item => this.sameSelectionId(item, option.id))) {
                    selected.push(option.id);
                }
            });
            cell.valueId = selected.length ? JSON.stringify(selected) : null;
        } else {
            const stored = this.readStoredJson(cell.value);
            const existing = cell.data.find(option => this.sameSelectionId(option.id, cell.valueId));
            if (existing) {
                cell.valueId = existing.id;
            } else {
                if (this.isStoredOption(stored) && this.sameSelectionId(stored.id, cell.valueId)) {
                    cell.data.push(stored);
                    cell.valueId = stored.id;
                } else {
                    cell.valueId = null;
                }
            }
        }
        cell.value = cell.valueId;
    }

    fillDataToModel(customers: any, data: FormData) {
        this.isOnAutoNumber = this.createContractService.isOnAutoNumber.getValue();
        this.isBack = false;
        const dataForm = JSON.parse(JSON.stringify(data));
        let rows = data.rows ? data.rows : [];

        rows.forEach((item, index) => {
            if (item.cells.value && item.cells.value.dataType.code == "contract_code") {
                item.cells.value['value'] =  '';
                this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, '');
                if (this.isOnAutoNumber) {
                    item.cells.value['value'] = this.configAutoNumber ? this.configAutoNumber : '';
                    const errorMess = this.message.getMessage("handsontable.error.contract_code_note_auto_number");
                    if (this.isUpdate && this.refreshContractCode) {
                        item.cells.value['value'] = this.createContractService.configAutoNumber.value;
                        this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
                    };
                    if (!this.isUpdate) {
                        item.cells.value['value'] = this.createContractService.configAutoNumber.value;
                        this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
                    };
                    //hien mahd moi neu doi mau + nhan refresh
                    if (this.isUpdate && this.refreshContractCode && !this.isChangeTypeDocAndTemp) {
                        item.cells.value['value'] = this.createContractService.configAutoNumber.value;
                        this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
                    }
                    if (this.isUpdate && !this.refreshContractCode && !this.isChangeTypeDocAndTemp) {
                        item.cells.value['value'] = this.contract.code ? this.contract.code : '';
                    }
                } else {
                    item.cells.value['value'] = this.contract.code ? this.contract.code : '';
                }
            }
        })
        const fields = this.createContractService.fieldData ? this.createContractService.fieldData : [];
        const mergeCells = [];
        const codeWithData: any = {};
        let indexBonus = 0;
        const dataChange = [];
        const lang = localStorage.getItem('__language');
        for (const [index, row] of rows.entries()) {
            row.cells.orderNo = { value: row.orderNo };
            let field;
            if (row && row.fieldId) {
                field = fields.find(field => field.fieldId && field.fieldId === row.fieldId);
                if (field) {
                    row.field = JSON.parse(JSON.stringify(field));
                }
            }
            if (row.children) {
                mergeCells.push({
                    row: index + indexBonus,
                    col: 1,
                    rowspan: 1,
                    colspan: 2
                });
                row.childCode = row.children.map(item => {
                    return item.cells.value.code
                });
                const cacheChild = row.children.map((child, indexChild) => {
                    if (child.fieldId && field && field.children) {
                        const fieldChildFind = field.children.find(fieldChild => fieldChild.fieldId === child.fieldId);
                        if (fieldChildFind) {
                            child.field = JSON.parse(JSON.stringify(fieldChildFind));
                        }
                    }
                    const cellValueChild = child.cells && child.cells.value ? child.cells.value : null;
                    child.cells.orderNo = { value: row.orderNo + '.' + child.orderNo };
                    if (child.cells && child.cells.name) {
                        child.cells.name.className = 'child';
                    }

                    if (cellValueChild.type && (cellValueChild.type.code === HandsontableFieldTypeContrants.number || cellValueChild.type.code === HandsontableFieldTypeContrants.moneyFormat)) {
                        if (cellValueChild.value) {
                            let splitNumber;
                            let check = true;
                            if (lang === 'vi') {
                                splitNumber = cellValueChild.value.toString().split('.');
                            } else {
                                splitNumber = cellValueChild.value.toString().split(',');
                            }
                            if (splitNumber && splitNumber.length) {
                                splitNumber.forEach((item, indexNumber) => {
                                    if (!/^([0-9]){3}$/.test(item) && indexNumber < splitNumber.length - 1 && indexNumber > 0) {
                                        check = false;
                                    }
                                    if (indexNumber === 0 && !/^([0-9]){1,3}$/.test(item) && splitNumber.length > 1) {
                                        check = false;
                                    }
                                    if (indexNumber === splitNumber.length - 1) {
                                        if (indexNumber === 0) {
                                            switch (lang) {
                                                case 'vi': if (!/^([0-9]{1,3})(,[0-9]*)?$/.test(item)) {
                                                    check = false;
                                                }
                                                    break;
                                                case 'en': if (!/^([0-9]{1,3})(.[0-9]*)?$/.test(item)) {
                                                    check = false;
                                                }
                                                    break;
                                                default: break;
                                            }
                                        } else {
                                            switch (lang) {
                                                case 'vi': if (!/^([0-9]{3})(,[0-9]*)?$/.test(item)) {
                                                    check = false;
                                                }
                                                    break;
                                                case 'en': if (!/^([0-9]{3})(.[0-9]*)?$/.test(item)) {
                                                    check = false;
                                                }
                                                    break;
                                                default: break;
                                            }
                                        }
                                    }
                                })
                            }
                            if (check) {
                                cellValueChild.value = splitNumber.join('').replace(',', '.');
                            }
                        } else if (!this.negotiationMode) {
                            cellValueChild.value = '0';
                        }
                    }

                    if (cellValueChild.type && cellValueChild.type.code === HandsontableFieldTypeContrants.numberPercent) {
                        if (cellValueChild.value) {
                            if (/^([0-9]*)(,[0-9]*)?\%?$/.test(cellValueChild.value) && lang === 'vi') {
                                cellValueChild.value = cellValueChild.value.replace(',', '.').replace('%', '');
                            } else if (/^([0-9]*)(.[0-9]*)?\%?$/.test(cellValueChild.value) && lang === 'en') {
                                cellValueChild.value = cellValueChild.value.replace('%', '');
                            }
                        }
                    }

                    this.restoreSelectionCell(cellValueChild, index + indexBonus + indexChild + 1, dataChange);

                    if (cellValueChild && cellValueChild.dataType && cellValueChild.dataType.code === DATA_TYPE_CUSTOMER && cellValueChild.category && cellValueChild.category.code === CATEGORY_CUSTOMER_CODE) {
                        if (cellValueChild.valueId) {
                            const result = customers.filter(item => item.customerId === cellValueChild.valueId);
                            if (result && result.name && result.name !== cellValueChild.value) {
                                result.name = cellValueChild.value;
                            }
                        }
                        cellValueChild.data = customers.map(customer => {
                            customer.value = customer.name ? customer.name : '';
                            return customer
                        });
                    } else if (cellValueChild && cellValueChild.dataType && cellValueChild.dataType.code === DATA_TYPE_SELECT && cellValueChild.type && cellValueChild.type.code === TYPE_COMBOBOX && cellValueChild.valueId) {
                        const selected = cellValueChild.data ? cellValueChild.data.find(item => item.id === cellValueChild.valueId) : null;
                        if (!selected) {
                            if (cellValueChild.data) {
                                cellValueChild.data.push({ value: cellValueChild.value, id: cellValueChild.valueId });
                            } else {
                                cellValueChild.data = [{ value: cellValueChild.value, id: cellValueChild.valueId }];
                            }
                        }
                    } else if (cellValueChild && cellValueChild.dataType && cellValueChild.dataType.code === DATA_TYPE_REF && cellValueChild.valueId) {
                        const selected = cellValueChild.data ? cellValueChild.data.find(item => item.id === cellValueChild.valueId) : null;
                        if (selected) {
                            if (cellValueChild.value != selected.value) {
                                dataChange.push([index + indexBonus + indexChild + 1, 2, selected.value]);
                            }
                        }
                    }

                    if (cellValueChild.fixedFunction && cellValueChild.fixedFunction.code === 'NUMBER_TO_MONEY_TEXT' && cellValueChild.fixedFieldCode) {
                        const splitCode = cellValueChild.fixedFieldCode.split('-');
                        cellValueChild.fixedFieldCode = splitCode && splitCode[0] ? splitCode[0] : splitCode;
                        cellValueChild.fixedMoneyCode = splitCode && splitCode[1] ? splitCode[1] : (cellValueChild.fixedMoneyCode ? cellValueChild.fixedMoneyCode : null);
                    }

                    if (cellValueChild.value && !codeWithData[cellValueChild.code]) {
                        if (cellValueChild.type && cellValueChild.type.code === TYPE_COMBOBOX) {
                            cellValueChild.data = cellValueChild.data ? cellValueChild.data : [];
                            const optionSelected = cellValueChild.data.find(item => item.value === cellValueChild.value);
                            if (optionSelected) {
                                codeWithData[cellValueChild.code] = optionSelected;
                            } else {
                                cellValueChild.value = null;
                            }
                        } else {
                            codeWithData[cellValueChild.code] = cellValueChild.value;
                        }
                    }
                    return child;
                });
                rows = [
                    ...rows.slice(0, index + indexBonus + 1),
                    ...cacheChild,
                    ...rows.slice(index + indexBonus + 1)
                ];
                indexBonus += cacheChild.length;
                continue;
            }
            const cellValue = row.cells && row.cells.value ? row.cells.value : null;
            if (cellValue.type && (cellValue.type.code === HandsontableFieldTypeContrants.number || cellValue.type.code === HandsontableFieldTypeContrants.moneyFormat)) {
                if (cellValue.value) {
                    let splitNumber;
                    let check = true;
                    if (lang === 'vi') {
                        splitNumber = cellValue.value.toString().split('.');
                    } else {
                        splitNumber = cellValue.value.toString().split(',');
                    }
                    if (splitNumber && splitNumber.length) {
                        splitNumber.forEach((item, indexNumber) => {
                            if (!/^([0-9]){3}$/.test(item) && indexNumber < splitNumber.length - 1 && indexNumber > 0) {
                                check = false;
                            }
                            if (indexNumber === 0 && !/^([0-9]){1,3}$/.test(item) && splitNumber.length > 1) {
                                check = false;
                            }
                            if (indexNumber === splitNumber.length - 1) {
                                if (indexNumber === 0) {
                                    switch (lang) {
                                        case 'vi': if (!/^([0-9]{1,3})(,[0-9]*)?$/.test(item)) {
                                            check = false;
                                        }
                                            break;
                                        case 'en': if (!/^([0-9]{1,3})(.[0-9]*)?$/.test(item)) {
                                            check = false;
                                        }
                                            break;
                                        default: break;
                                    }
                                } else {
                                    switch (lang) {
                                        case 'vi': if (!/^([0-9]{3})(,[0-9]*)?$/.test(item)) {
                                            check = false;
                                        }
                                            break;
                                        case 'en': if (!/^([0-9]{3})(.[0-9]*)?$/.test(item)) {
                                            check = false;
                                        }
                                            break;
                                        default: break;
                                    }
                                }
                            }
                        })
                    }
                    if (check) {
                        cellValue.value = splitNumber.join('').replace(',', '.');
                    }
                } else if (!this.negotiationMode) {
                    cellValue.value = '0';
                }
            }
            if (cellValue.type && cellValue.type.code === HandsontableFieldTypeContrants.numberPercent) {
                if (cellValue.value) {
                    if (/^([0-9]*)(,[0-9]*)?\%?$/.test(cellValue.value) && lang === 'vi') {
                        cellValue.value = cellValue.value.replace(',', '.').replace('%', '').trim();
                    } else if (/^([0-9]*)(.[0-9]*)?\%?$/.test(cellValue.value) && lang === 'en') {
                        cellValue.value = cellValue.value.replace('%', '').trim();
                    }
                }
            }
            this.restoreSelectionCell(cellValue, index + indexBonus, dataChange);

            if (cellValue && cellValue.dataType && cellValue.dataType.code === DATA_TYPE_CUSTOMER && cellValue.category && cellValue.category.code === CATEGORY_CUSTOMER_CODE) {
                if (cellValue.valueId) {
                    const result = customers.filter(item => item.customerId === cellValue.valueId);
                    if (result && result.name && result.name !== cellValue.value) {
                        result.name = cellValue.value;
                    }
                }
                cellValue.data = customers.map(customer => {
                    customer.value = customer.name ? customer.name : '';
                    return customer
                });
            } else if (cellValue && cellValue.dataType && cellValue.dataType.code === DATA_TYPE_SELECT && cellValue.type && cellValue.type.code === TYPE_COMBOBOX && cellValue.valueId) {
                const selected = cellValue.data ? cellValue.data.find(item => item.id === cellValue.valueId) : null;
                if (!selected) {
                    if (cellValue.data) {
                        cellValue.data.push({ value: cellValue.value, id: cellValue.valueId });
                    } else {
                        cellValue.data = [{ value: cellValue.value, id: cellValue.valueId }];
                    }
                }
            } else if (cellValue && cellValue.dataType && cellValue.dataType.code === DATA_TYPE_REF && cellValue.valueId) {
                const selected = cellValue.data ? cellValue.data.find(item => item.id === cellValue.valueId) : null;
                if (selected) {
                    if (cellValue.value != selected.value) {
                        dataChange.push([index + indexBonus, 2, selected.value]);
                    }
                }
            }

            if (cellValue.fixedFunction && cellValue.fixedFunction.code === 'NUMBER_TO_MONEY_TEXT' && cellValue.fixedFieldCode) {
                const splitCode = cellValue.fixedFieldCode.split('-');
                cellValue.fixedFieldCode = splitCode && splitCode[0] ? splitCode[0] : splitCode;
                cellValue.fixedMoneyCode = splitCode && splitCode[1] ? splitCode[1] : (cellValue.fixedMoneyCode ? cellValue.fixedMoneyCode : null);
            }

            if (cellValue.value && !codeWithData[cellValue.code]) {
                if (cellValue.type && cellValue.type.code === TYPE_COMBOBOX) {
                    cellValue.data = cellValue.data ? cellValue.data : [];
                    const optionSelected = cellValue.data.find(item => item.value === cellValue.value);
                    if (optionSelected) {
                        codeWithData[cellValue.code] = optionSelected;
                    } else {
                        cellValue.value = null;
                    }
                } else {
                    codeWithData[cellValue.code] = cellValue.value;
                }
            }
        }

        for (const [rowIndex, row] of rows.entries()) {
            const cellValue = row.cells && row.cells.value ? row.cells.value : null;
            if (!cellValue) {
                continue;
            }

            // tham chieu danh muc
            if (cellValue.relatedFieldCode && codeWithData[cellValue.relatedFieldCode] && cellValue.dataType && cellValue.dataType.code === 'ref') {
                const objectData = codeWithData[cellValue.relatedFieldCode];
                cellValue.filterData = cellValue.data.filter(item => {
                    return item.parent_code === objectData.code
                });
                continue;
            }
        }

        dataForm.rows = rows;
        dataForm.colHeaders = this._hotSettingsDefault.colHeaders as string[];
        this.data = dataForm;

        const settings = this.handsontableService.convertTableSettings(this._hotSettingsDefault, dataForm);
        this._hotRegister.getInstance('negotiation-template').updateSettings(settings.settings);
        rows.forEach((element, index) => {
            if(element.cells.value && element.cells.value.dataType.code === CONTRACT_CODE_TYPE && !element.cells.value.value){
                let errorMess = '';
                if(this.isOnAutoNumber) {
                    errorMess = this.message.getMessage("handsontable.error.contract_code_note_auto_number");
                }
                this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
            }
            if(this.isOnAutoNumber) {
                if(element.cells.value && element.cells.value.dataType.code === CONTRACT_CODE_TYPE) {
                    const errorMess = this.message.getMessage("handsontable.error.contract_code_note_auto_number");
                  if(this.createContractService.refreshContractCode.getValue() == true && this.isUpdate){
                    this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
                  }
                  if(!this.isUpdate) {
                    this._hotRegister.getInstance('negotiation-template').setDataAtCell(index, 3, errorMess);
                  }
                }
            }
        });
        if (settings.hiddenRows) {
            const hiddenRowsPlugin = this._hotRegister.getInstance('negotiation-template').getPlugin('hiddenRows');
            hiddenRowsPlugin.hideRows(settings.hiddenRows);
        }
        if (mergeCells.length) {
            setTimeout(() => {
                if (this.destroyed) {
                    return;
                }
                const mergeCellPlugin = this._hotRegister.getInstance('negotiation-template').getPlugin('mergeCells');
                mergeCells.forEach(item => {
                    mergeCellPlugin.merge(item.row, 1, item.row, 2);
                })
            })
        }
        this._hotRegister.getInstance('negotiation-template').render();
        if (dataChange.length) {
            setTimeout(() => {
                if (this.destroyed) {
                    return;
                }
                this._hotRegister.getInstance('negotiation-template').setDataAtCell(dataChange, 'first');
                this.showMissingRequiredFields();
                this.formReady.emit();
            }, 500);
        } else {
            this.showMissingRequiredFields();
            this.formReady.emit();
        }
    }

    private showMissingRequiredFields() {
        if (!this.data || !this.data.rows.some(row => row.cells && row.cells.value && row.cells.value.missingRequired)) {
            return;
        }
        const instance = this._hotRegister.getInstance('negotiation-template');
        if (!instance || instance.isDestroyed || this.destroyed) {
            return;
        }
        instance.validateCells(valid => {
            if (this.destroyed || instance.isDestroyed) {
                return;
            }
            this.createContractService.isValidForm.next(valid);
            let firstInvalidRow = -1;
            this.data.rows.forEach((row, index) => {
                if (this.canEditNegotiationField(row.field) &&
                    row.cells && row.cells.value && row.cells.value.missingRequired &&
                    instance.getCellMeta(index, 2).valid === false) {
                    instance.getPlugin('hiddenRows').showRow(index);
                    if (firstInvalidRow < 0) {
                        firstInvalidRow = index;
                    }
                }
            });
            if (firstInvalidRow >= 0) {
                instance.selectCell(firstInvalidRow, 2);
            }
            instance.render();
        });
    }

    getEditableValuesSnapshot(): string {
        const instance = this._hotRegister.getInstance('negotiation-template');
        if (!instance || instance.isDestroyed || !this.data || this.destroyed) {
            return null;
        }
        const editor = instance.getActiveEditor();
        const values = this.data.rows.reduce((result, row, index) => {
            const group = (row.children && row.children.length > 0) ||
                (row.field && row.field.fieldConfig && row.field.fieldConfig.group);
            if (this.canEditNegotiationField(row.field) && !group) {
                const editingValue = editor && editor.isOpened() && editor.row === index && editor.col === 2;
                const value = editingValue ? editor.getValue() : instance.getDataAtCell(index, 2);
                result.push(value == null ? '' : typeof value === 'number' ? String(value) : value);
            }
            return result;
        }, []);
        return JSON.stringify(values);
    }

    hasCompleteRequiredFields(): boolean {
        const instance = this._hotRegister.getInstance('negotiation-template');
        if (!instance || instance.isDestroyed || !this.data || this.destroyed) {
            return false;
        }
        const editor = instance.getActiveEditor();
        return this.data.rows.every((row, index) => {
            const group = (row.children && row.children.length > 0) ||
                (row.field && row.field.fieldConfig && row.field.fieldConfig.group);
            if (!this.canEditNegotiationField(row.field) || group ||
                !this.handsontableService.isRequiredField(row)) {
                return true;
            }
            const editingValue = editor && editor.isOpened() && editor.row === index && editor.col === 2;
            const value = editingValue ? editor.getValue() : instance.getDataAtCell(index, 2);
            return this.handsontableService.hasRequiredValue(row, value);
        });
    }

    validateForm(callback: (valid: boolean) => void): void {
        const instance = this._hotRegister.getInstance('negotiation-template');
        if (!instance || instance.isDestroyed || this.destroyed) {
            this.createContractService.isValidForm.next(false);
            callback(false);
            return;
        }
        const validateCommittedValues = () => {
            if (this.destroyed || instance.isDestroyed) {
                return;
            }
            instance.validateCells(() => {
                if (this.destroyed || instance.isDestroyed) {
                    return;
                }
                const invalidRows: number[] = [];
                for (let row = 0; row < instance.countRows(); row++) {
                    if (this.canEditNegotiationField(this.data.rows[row] && this.data.rows[row].field) &&
                        instance.getCellMeta(row, 2).valid === false) {
                        invalidRows.push(row);
                    }
                }
                // Keep the old list during validation so afterValidate can clear corrected errors.
                this.handsontableService.listRowInvalid = invalidRows;
                const valid = invalidRows.length === 0;
                this.createContractService.isValidForm.next(valid);
                if (!valid && invalidRows.length) {
                    const row = invalidRows[0];
                    instance.getPlugin('hiddenRows').showRow(row);
                    instance.selectCell(row, 2);
                }
                instance.render();
                callback(valid);
            });
        };
        const editor = instance.getActiveEditor();
        if (editor && editor.isOpened()) {
            // Wait for the editor's asynchronous validation before exporting its latest value.
            editor.finishEditing(false, false, () => {
                if (this.destroyed || instance.isDestroyed) {
                    return;
                }
                if (editor.isOpened()) {
                    this.createContractService.isValidForm.next(false);
                    callback(false);
                    return;
                }
                validateCommittedValues();
            });
        } else {
            validateCommittedValues();
        }
    }

    getPreviewFields() {
        const originalData = this.data;
        this.data = JSON.parse(JSON.stringify(originalData));
        try {
            const fields = this.saveModelData();
            if (!this.negotiationMode) {
                return fields;
            }
            const originals = originalData.rows.filter(row => row.cells && row.cells.orderNo &&
                !/^[0-9]{1,}\.[0-9]{1,}$/.test(row.cells.orderNo.value.toString())).map(row => row.field);
            return this.mergeNegotiationValues(originals, fields);
        } finally {
            this.data = originalData;
        }
    }

    private canEditNegotiationField(field: any): boolean {
        return !this.negotiationMode || !!(field && field.customerId != null &&
            String(field.customerId) !== '' &&
            this.negotiationCustomerIds.some(id => String(id) === String(field.customerId)));
    }

    private isCalculatedField(field: any): boolean {
        const config = field && field.fieldConfig;
        return !!(config && (config.formula || (config.fixedFunction &&
            ['SUM', 'NUMBER_TO_MONEY_TEXT', 'NUMBER_TO_TEXT'].includes(config.fixedFunction.code))));
    }

    private mergeNegotiationValues(originals: any[], updates: any[]): any[] {
        return originals.map((original, index) => {
            const updated = updates[index] || original;
            const field = this.canEditNegotiationField(original) || this.isCalculatedField(original)
                ? { ...original, value: updated.value, valueId: updated.valueId }
                : { ...original };
            if (original.children) {
                field.children = this.mergeNegotiationValues(original.children, updated.children || []);
            }
            return field;
        });
    }

    saveModelData() {
        const instance = this._hotRegister.getInstance('negotiation-template');
        const listValue = instance.getDataAtCol(2);
        if (!this.data) {
            return [];
        }
        const dataField = this.data.rows.filter(row => {
            return row.cells && row.cells.orderNo && !/^[0-9]{1,}\.[0-9]{1,}$/.test(row.cells.orderNo.value.toString());
        });
        this.data.rows = dataField;

        const fields = dataField.map(row => {
            return row.field
        });
        let amountChild = 0;

        for (const [indexField, field] of fields.entries()) {
            const index = indexField + amountChild;
            const cellValue = this.data.rows[indexField] && this.data.rows[indexField].cells && this.data.rows[indexField].cells.value ? this.data.rows[indexField].cells.value : null;
            if (this.canEditNegotiationField(field) || this.isCalculatedField(field)) {
                field.value = listValue[index] !== undefined ? (listValue[index] === -0 ? '0' : listValue[index]) : null;
                if (cellValue) {
                    cellValue.value = field.value;
                }
                this.setDataToField(field, cellValue);
            }
            if (field.children) {
                for (const [indexFieldChild, child] of field.children.entries()) {
                    const indexChild = index + indexFieldChild + 1;
                    const cellValueChild = this.data.rows[indexField].children[indexFieldChild].cells && this.data.rows[indexField].children[indexFieldChild].cells.value ? this.data.rows[indexField].children[indexFieldChild].cells.value : null;
                    if (this.canEditNegotiationField(child) || this.isCalculatedField(child)) {
                        child.value = listValue[indexChild] !== undefined ? (listValue[indexChild] === 0 ? '0' : listValue[indexChild]) : null;
                        if (cellValueChild) {
                            cellValueChild.value = child.value;
                        }
                        this.setDataToField(child, cellValueChild);
                    }
                }
                amountChild += field.children.length;
            }
        }

        return fields;
    }

    setDataToField(field: any, cellValue: any) {
        if (!field || !field.fieldConfig) {
            return;
        }
        if (cellValue) {
            if (cellValue.formula && field.fieldConfig) {
                field.fieldConfig.formula = cellValue.formula;
            } else if (field.fieldConfig.formula && !cellValue.formula) {
                field.fieldConfig.formula = null;
            }
        }
        if (field.fieldConfig && field.fieldConfig.category && field.fieldConfig.category.code === CATEGORY_CUSTOMER_CODE) {
            const customer = this.recordUserSign.find(user => user.value === field.value);
            field.valueId = customer && customer.customerId ? customer.customerId : null;
            if (cellValue) {
                cellValue.valueId = customer && customer.customerId ? customer.customerId : null;
            }
        } else if (field.fieldConfig && field.fieldConfig.dataType && field.fieldConfig.dataType.code === DATA_TYPE_REF) {
            const option = cellValue.data.find(item => item.value == field.value);
            field.valueId = option && option.id ? option.id : null;
            if (cellValue) {
                cellValue.valueId = option && option.id ? option.id : null;
            }
        } else if (field.fieldConfig && field.fieldConfig.type && field.fieldConfig.type.code === HandsontableFieldTypeContrants.combobox && cellValue && cellValue.data) {
            const selected = cellValue.data.find(item => item.value === field.value) ||
                cellValue.data.find(item => typeof item.value === 'number' && String(item.value) === field.value);
            field.valueId = selected && selected.id != null ? selected.id : null;
            field.value = selected ? JSON.stringify(selected) : null;
            if (cellValue) {
                cellValue.valueId = field.valueId;
                cellValue.value = selected ? JSON.stringify(selected) : null;
            }
        } else if (field.fieldConfig && field.fieldConfig.type && field.fieldConfig.type.code === HandsontableFieldTypeContrants.radio && cellValue && cellValue.data) {
            const selected = cellValue.data.find(item => this.sameSelectionId(item.id, field.value));
            field.valueId = selected ? selected.id : null;
            field.value = selected ? JSON.stringify(selected) : null;
            if (cellValue) {
                cellValue.valueId = selected ? selected.id : null;
                cellValue.value = selected ? JSON.stringify(selected) : null;
            }
        } else if (field.fieldConfig && field.fieldConfig.type && field.fieldConfig.type.code === HandsontableFieldTypeContrants.checkbox && cellValue && cellValue.data) {
            const checked = this.readStoredArray(field.value);
            const selected = cellValue.data.filter(item => checked.some(id => this.sameSelectionId(id, item.id)));
            field.valueId = selected.length ? JSON.stringify(selected.map(item => { return item.id })) : null;
            field.value = selected.length ? JSON.stringify(selected.map(item => { return item })) : null;
            if (cellValue) {
                cellValue.valueId = selected.length ? JSON.stringify(selected.map(item => { return item.id })) : null;
                cellValue.value = selected.length ? JSON.stringify(selected.map(item => { return item })) : null;
            }
        }
    }

    mapDataCustomer(localData: any, apiData: any) {
        const lang = localStorage.getItem('__language');
        let mapApiData = [];
        apiData.forEach(user => {
            const dataLocal = localData.find(userLocal => userLocal.name === user.name);
            for (const key of Object.keys(user)) {
                if ((key.toLocaleLowerCase().includes('birthday') || key.toLocaleLowerCase().includes('date')) && user[key]) {
                    user[key] = format(user[key], 'DD/MM/YYYY');
                }
                if ((key === 'certificateId' || key === 'representativeCertificateId') && user[key]) {
                    const certificate = this.certificateType.find(item => item.id === user[key]);
                    if (certificate && certificate.langs) {
                        const text = certificate.langs.find(item => item.language === lang);
                        user[key] = text && text.name ? text.name : null;
                    }
                }
                if (key.toLocaleLowerCase().includes('sex') && user[key] !== null) {
                    user[key] = user[key] ? this.message.getMessage('form.male') : this.message.getMessage('form.female');
                }
            }
            LIST_FIELD_CAN_CHANGE.forEach(key => {
                user[key] = dataLocal && dataLocal[key] ? dataLocal[key] : (user[key] ? user[key] : null);
            })
            mapApiData.push(user);
        })
        mapApiData = mapApiData.sort((a, b) => { return a.order - b.order });

        return mapApiData;
    }

}
